// deployment_class: customer_env_allowed
/**
 * Login — the Kathedral.ai glass landing + email/password authentication entry.
 *
 * VISUAL (2026-07-13): the "glass login landing" — a frosted-glass panel over a
 * full-bleed institutional landscape (Kathedral Glass Toolbox; ratified by E3).
 * The visual layer is new; ALL authentication behavior + accessibility below is
 * preserved verbatim from the prior password-login (Task 11a) — no regression.
 *
 * Two modes in a single form, toggled in place:
 *   - "signin"  → authClient.signIn.email (default)
 *   - "apply"   → authClient.signUp.email (create an account — "Apply for Account")
 * Password-only baseline; MFA / passkey are Task 11b. "Apply for Account" is the
 * customer-facing wording for account creation (E3 2026-07-13); the mechanism is
 * unchanged (Better Auth signup + required email verification — the account is
 * inert until the email is verified, matching the vetted-onboarding posture).
 *
 * On successful SIGN IN the user is navigated to a SAFE returnTo (open-redirect
 * guarded per Spec 12 §6.5). On APPLY, because the V3 server requires email
 * verification (`emailAndPassword.requireEmailVerification: true`), a new account
 * does NOT get a session — we surface a "check your email" status and leave the
 * user on the form to sign in once verified.
 *
 * (Self-serve password reset — "Forgot password?" — lands in Wave 2.)
 *
 * Accessibility (WCAG 2.2 AA — Design System §9):
 *   - single <main> landmark + single <h1> (visually-hidden; the wordmark carries
 *     the brand, the h1 carries the page purpose for assistive tech)
 *   - every input has an associated <label for> (visually-hidden, placeholder-in-field)
 *   - submit errors render in role="alert" and the offending field gets
 *     aria-invalid + aria-describedby pointing at the error; focus moves to it
 *   - polite aria-live status region announces progress
 *   - fully keyboard operable; design tokens only (no hardcoded colors)
 */
import { useEffect, useId, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';

/**
 * Safe returnTo extraction. Per Spec 12 §6.5: only allow returnTo URLs that
 * are same-origin, path-only — prevents an open-redirect attack via
 * /auth/login?returnTo=https://evil.example.com.
 */
export function getReturnTo(search: string): string {
  const params = new URLSearchParams(search);
  const raw = params.get('returnTo');
  if (!raw) return '/';
  // Must be a same-origin, path-only URL. Reject anything a browser could
  // normalize into an OFF-origin navigation: non-path-leading, protocol-relative
  // (`//`), backslash tricks (`/\` and any `\`, which browsers treat like `/`),
  // and embedded control chars. (Fort Knox Ring 1 · Wave 0 open-redirect song
  // caught the original guard allowing `/\evil.com`.)
  if (!raw.startsWith('/')) return '/';
  if (raw.startsWith('//')) return '/';
  if (raw.includes('\\')) return '/';
  if (/[\u0000-\u001f]/.test(raw)) return '/';
  return raw;
}

/** Server-side floor (better-auth-config.ts: minPasswordLength: 12). */
const MIN_PASSWORD_LENGTH = 12;

type Mode = 'signin' | 'apply';

export function Login(): JSX.Element {
  const { login, signup } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  // Stable ids so labels + aria-describedby wire up deterministically.
  const emailId = useId();
  const passwordId = useId();
  const errorId = useId();

  const isApply = mode === 'apply';

  // After verifying their email the user lands back here at
  // `/auth/login?verified=1` (same-origin callbackURL). Surface a polite
  // confirmation in the existing status region so they know they can sign in now.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('verified') === '1') {
      setStatus('Email verified — you can sign in now.');
    }
  }, [location.search]);

  function switchMode(next: Mode): void {
    setMode(next);
    setError(null);
    setStatus(null);
  }

  function focusOffendingField(field: 'email' | 'password'): void {
    const ref = field === 'email' ? emailRef : passwordRef;
    ref.current?.focus();
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setStatus(null);

    // Client-side validation mirrors the server floor so the user gets an
    // immediate, accessible error instead of a round-trip rejection.
    if (isApply && password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      focusOffendingField('password');
      return;
    }

    setSubmitting(true);
    try {
      if (isApply) {
        setStatus('Submitting your application…');
        await signup({ email, password });
        setStatus('Account created. Check your email to verify, then sign in.');
        // Drop back to sign-in mode so the verified user can proceed.
        setMode('signin');
        setPassword('');
      } else {
        setStatus('Signing in…');
        await login({ email, password });
        navigate(getReturnTo(location.search), { replace: true });
      }
    } catch (err) {
      setStatus(null);
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      // Move focus to the credential field most likely at fault.
      focusOffendingField(isApply ? 'email' : 'password');
    } finally {
      setSubmitting(false);
    }
  }

  const heading = isApply ? 'Apply for a Kathedral.ai account' : 'Sign in';
  const submitLabel = isApply ? 'Apply for account' : 'Sign in';
  const busyLabel = isApply ? 'Submitting application…' : 'Signing in…';

  return (
    <main className="glass-stage">
      <div className="glass-card w-full max-w-sm p-card">
        <div
          role="img"
          aria-label="Kathedral.ai"
          className="kth-wordmark-row kth-wordmark--login mb-rhythm"
        >
          <img
            src="/kathedral-logo.png"
            alt=""
            aria-hidden="true"
            className="kth-wordmark-icon kth-wordmark-icon--login h-[50px] w-[50px]"
          />
          <span aria-hidden="true" className="kth-wordmark text-[35px] font-normal tracking-[-0.015em]">
            <span className="kth-wordmark-mark">Kathedral</span>
            <span className="kth-wordmark-tld">.ai</span>
          </span>
        </div>

        {/* Visually-hidden page heading — the wordmark is the visible brand. */}
        <h1 className="sr-only">{heading}</h1>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4" aria-label={heading} noValidate>
          <div className="glass-control">
            <label htmlFor={emailId} className="sr-only">
              User
            </label>
            <svg className="glass-control-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <circle cx="12" cy="8" r="3.6" />
              <path d="M12 13.5c-4 0-7 2.6-7 6.1 0 .22.18.4.4.4h13.2a.4.4 0 0 0 .4-.4c0-3.5-3-6.1-7-6.1Z" />
            </svg>
            <input
              id={emailId}
              ref={emailRef}
              type="text"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="glass-input"
              placeholder="Username"
              autoComplete="username"
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errorId : undefined}
            />
          </div>

          <div className="glass-control">
            <label htmlFor={passwordId} className="sr-only">
              Password
            </label>
            <svg className="glass-control-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 2.5a4.5 4.5 0 0 0-4.5 4.5v2H7a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-.5V7A4.5 4.5 0 0 0 12 2.5Zm2.5 6.5h-5V7a2.5 2.5 0 0 1 5 0v2Z" />
            </svg>
            <input
              id={passwordId}
              ref={passwordRef}
              type="password"
              required
              minLength={isApply ? MIN_PASSWORD_LENGTH : undefined}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="glass-input"
              placeholder="Password"
              autoComplete={isApply ? 'new-password' : 'current-password'}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errorId : undefined}
            />
          </div>

          {isApply && (
            <p className="text-body-xs text-warm-gray-d glass-text">
              At least {MIN_PASSWORD_LENGTH} characters.
            </p>
          )}

          {error && (
            <p id={errorId} role="alert" className="text-body-s text-coral-d glass-text">
              {error}
            </p>
          )}

          {/* Polite status announcements. Always present so the live region is
              registered before updates. */}
          <p role="status" aria-live="polite" className="text-body-s text-warm-gray-d glass-text">
            {status ?? ''}
          </p>

          <button type="submit" disabled={submitting} className="glass-submit mt-1">
            {submitting ? busyLabel : submitLabel}
          </button>
        </form>

        <p className="mt-rhythm text-body-s text-warm-gray-d text-center glass-text">
          {isApply ? (
            <>
              Already have an account?{' '}
              <button type="button" onClick={() => switchMode('signin')} className="glass-link font-medium">
                Sign in
              </button>
            </>
          ) : (
            <>
              New to Kathedral?{' '}
              <button type="button" onClick={() => switchMode('apply')} className="glass-link font-medium">
                Apply for Account
              </button>
            </>
          )}
        </p>
      </div>
    </main>
  );
}
